import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  static #observer = null;

  connectedCallback() {
    super.connectedCallback();

    VideoBackgroundComponent.#getObserver().observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    VideoBackgroundComponent.#getObserver().unobserve(this);
  }

  /**
   * Points the <source> elements at their real URL and starts buffering.
   * Deferred until the component is near the viewport so off-screen
   * background videos don't compete with above-the-fold resources.
   */
  #loadVideo() {
    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }

  /**
   * @returns {IntersectionObserver}
   */
  static #getObserver() {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const component = /** @type {VideoBackgroundComponent} */ (entry.target);

            component.#loadVideo();
            this.#observer?.unobserve(component);
          }
        },
        { rootMargin: '200px' }
      );
    }

    return this.#observer;
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
